import React, { useEffect, useState } from 'react';
import { FileText, Download, Search, ShieldCheck, HardHat, Zap, Ruler, Flame, Building } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const documents = [
  {
    id: 1,
    Icon: FileText,
    category: 'foncier',
    title: {
      fr: "Guide foncier — Sécuriser sa parcelle à Kinshasa",
      en: 'Land guide — Securing your plot in Kinshasa',
    },
    desc: {
      fr: "Titres, cadastre, Conservation des Titres Immobiliers, vérification de la chaîne de propriété.",
      en: 'Titles, cadastre, Land Titles Conservancy, ownership chain verification.',
    },
    tag: { fr: 'FONCIER', en: 'LAND' },
    size: 'PDF · 5,9 Mo',
    url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/nphdrd4r_ARRETE-117.pdf',
  },
  {
    id: 2,
    Icon: Ruler,
    category: 'urbanisme',
    title: {
      fr: "Règles d'urbanisme applicables à Kinshasa",
      en: 'Urban planning rules applicable in Kinshasa',
    },
    desc: {
      fr: "Zonage, hauteurs autorisées, retraits, coefficient d'occupation du sol par commune.",
      en: 'Zoning, allowed heights, setbacks, plot ratio per commune.',
    },
    tag: { fr: 'URBANISME', en: 'URBAN PLANNING' },
    size: 'PDF · 13 Ko',
    url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/sp0d4u2t_permis-de-construire-rdc-gupec.pdf',
  },
  {
    id: 3,
    Icon: ShieldCheck,
    category: 'permis',
    title: {
      fr: "Constituer un dossier de permis de bâtir en RDC",
      en: 'Building permit application file in DRC',
    },
    desc: {
      fr: "Pièces à fournir, taxes DGRK, circuit administratif Hôtel de Ville de Kinshasa.",
      en: 'Required documents, DGRK taxes, administrative circuit at Kinshasa City Hall.',
    },
    tag: { fr: 'PERMIS', en: 'PERMIT' },
    size: 'PDF · 980 Ko',
  },
  {
    id: 8,
    Icon: HardHat,
    category: 'hse',
    title: {
      fr: "HSE chantier — Bonnes pratiques et EPI",
      en: 'Site HSE — Best practices and PPE',
    },
    desc: {
      fr: "Plan de prévention, équipements de protection individuelle, déclarations INPP et CNSS.",
      en: 'Prevention plan, personal protective equipment, INPP and CNSS declarations.',
    },
    tag: { fr: 'HSE', en: 'HSE' },
    size: 'PDF · 10 Ko',
    url: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/mdoqkgl8_securite-chantier-btp.pdf',
  },
];

const filters = [
  { id: 'all', label: { fr: 'Tous', en: 'All' } },
  { id: 'foncier', label: { fr: 'Foncier', en: 'Land' } },
  { id: 'urbanisme', label: { fr: 'Urbanisme', en: 'Urban planning' } },
  { id: 'permis', label: { fr: 'Permis', en: 'Permits' } },
  { id: 'hse', label: { fr: 'HSE', en: 'HSE' } },
];

const EspaceDocumentsPage = () => {
  const { language } = useLanguage();
  const [active, setActive] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const filtered = documents.filter((d) => {
    const catOk = active === 'all' || d.category === active;
    const s = q.trim().toLowerCase();
    if (!s) return catOk;
    return catOk && (d.title[language] || d.title.fr).toLowerCase().includes(s);
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[240px]" data-testid="documents-page">
        {/* Hero */}
        <section className="relative h-[380px] md:h-[460px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/er1kmqui_IMG_6324.jpg')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <span className="inline-block text-orange-400 font-semibold tracking-widest text-sm mb-3">
              {t(language, 'GCI · RESSOURCES', 'GCI · RESOURCES')}
            </span>
            <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
              {t(language, 'Espace documents', 'Documents area')}
            </h1>
            <div className="mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(
                language,
                "Retrouvez ici les principaux textes, guides pratiques et modèles utiles pour comprendre le cadre d'une construction en RDC. Une base documentaire tenue à jour par nos équipes.",
                'Find here the main texts, practical guides and templates useful to understand the framework of construction in DRC. A documentary base kept up to date by our teams.'
              )}
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-6xl mx-auto px-6">
            {/* Search + filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t(language, 'Rechercher un document…', 'Search a document…')}
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-full focus:border-orange-500 focus:outline-none text-sm"
                  data-testid="documents-search"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-10" data-testid="documents-filters">
              {filters.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActive(f.id)}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-semibold transition-all ${
                    active === f.id
                      ? 'bg-[#702963] text-white shadow-md'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400 hover:text-orange-500'
                  }`}
                  data-testid={`filter-${f.id}`}
                >
                  {f.label[language]}
                </button>
              ))}
            </div>

            {/* List */}
            {filtered.length === 0 ? (
              <p className="text-center text-gray-500 py-16">
                {t(language, 'Aucun document ne correspond à votre recherche.', 'No document matches your search.')}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((d) => {
                  const Icon = d.Icon;
                  return (
                    <div
                      key={d.id}
                      className="bg-white border border-gray-200 rounded-sm p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                      data-testid={`document-card-${d.id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-sm text-orange-600">
                          <Icon className="w-6 h-6" strokeWidth={2} />
                        </div>
                        <span
                          className="text-[10px] font-bold tracking-widest uppercase text-white px-2 py-1 rounded-sm"
                          style={{ backgroundColor: '#702963' }}
                        >
                          {d.tag[language] || d.tag.fr}
                        </span>
                      </div>
                      <h3 className="text-base md:text-lg font-bold text-gray-900 leading-snug mb-2">
                        {d.title[language] || d.title.fr}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
                        {d.desc[language] || d.desc.fr}
                      </p>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-xs text-gray-500">{d.size}</span>
                        {d.url ? (
                          <a
                            href={d.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors"
                            data-testid={`document-download-${d.id}`}
                          >
                            <Download className="w-4 h-4" />
                            {t(language, 'Consulter', 'View')}
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              alert(
                                t(
                                  language,
                                  'Ce document sera bientôt disponible en téléchargement.',
                                  'This document will be available for download soon.'
                                )
                              )
                            }
                            className="inline-flex items-center gap-2 text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors"
                            data-testid={`document-download-${d.id}`}
                          >
                            <Download className="w-4 h-4" />
                            {t(language, 'Télécharger', 'Download')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-center text-xs text-gray-400 italic mt-10">
              {t(
                language,
                "Les documents proposés ici sont à titre indicatif et n'engagent pas la responsabilité de GCI. Consultez toujours les textes officiels en vigueur.",
                'Documents provided here are for guidance and do not engage GCI\u2019s liability. Always refer to the current official texts.'
              )}
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EspaceDocumentsPage;
